/**
 * Device-level login. Shown when `/api/session` reports unauthenticated.
 * Once accepted, the cookie persists ~1 year, so this screen is normally
 * only seen by the parent doing first-device setup.
 *
 * Per spec §5.6: copy is addressed to Eva ("Ask Mum or Dad to log in") —
 * the parent reading it still understands, and the framing avoids confusing
 * her if she ever lands here.
 */
import { useState } from "react";
import type { FormEvent } from "react";

import { PumpkinFigure } from "@/components/PumpkinFigure";
import { useAuth } from "@/context/AuthContext";
import "./LoginScreen.css";

export function LoginScreen() {
  const { login } = useAuth();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    const result = await login(password);
    setSubmitting(false);
    if (result.ok) {
      setPassword("");
      return;
    }
    if (result.reason === "invalid_password") {
      setError("That password didn't work. Try again.");
      setPassword("");
    } else if (result.reason === "network") {
      setError("Couldn't reach the spelling game. Check the connection?");
    } else if (result.reason === "server_misconfigured") {
      // Most likely cause: this is a preview/branch URL (e.g. main.<project>.pages.dev)
      // where Wrangler secrets weren't set. See docs/deploy.md.
      setError(
        "The server isn't set up right yet. Check you're on the production URL and that the secrets are in place.",
      );
    } else {
      setError("Something went wrong. Try again.");
    }
  }

  return (
    <main className="login">
      <div className="login__inner">
        <PumpkinFigure
          className="login__pumpkin pumpkin-idle"
          pose="waveHello"
          alt="Pumpkin the pangolin, waving hello"
        />
        <h1 className="login__title">Eva&rsquo;s Spelling Game</h1>
        <p className="login__subtitle">Ask Mum or Dad to log in.</p>

        <form className="login__form" onSubmit={handleSubmit} noValidate>
          <label className="login__label" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            className="login__input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            autoFocus
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
          <button
            type="submit"
            className="login__submit"
            disabled={submitting || password.length === 0}
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
          {error !== null && (
            <p className="login__error" role="alert">
              {error}
            </p>
          )}
        </form>
      </div>
    </main>
  );
}
